import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Accelerometer,
  AccelerometerMeasurement,
  Gyroscope,
  GyroscopeMeasurement,
} from 'expo-sensors';

import { isWeb } from 'utils/environment';

import { appMessages } from './utils';

type AccelerometerData = AccelerometerMeasurement;
type GyroscopeData = GyroscopeMeasurement;

export type SensorData = {
  accelerometer: AccelerometerData | null;
  gyroscope: GyroscopeData | null;
};

export type StartStopSensorsConfig = {
  accelerometer?: boolean;
  gyroscope?: boolean;
};

interface IUseSensorReturnType {
  startStopSensors: (config: StartStopSensorsConfig) => void;
  checkSensorsAvailability: () => Promise<boolean>;
}

interface UseSensorParams {
  handlePostMessageToIframe: (message: string) => void;
}

enum SENSOR {
  ACCELEROMETER = 'accelerometer',
  GYROSCOPE = 'gyroscope',
}

export const useSensors = ({
  handlePostMessageToIframe,
}: UseSensorParams): IUseSensorReturnType => {
  const sensorDataRef = useRef<SensorData>({
    accelerometer: null,
    gyroscope: null,
  });

  const [isAccelerometerActive, setIsAccelerometerActive] = useState<boolean>(false);
  const [isGyroscopeActive, setIsGyroscopeActive] = useState<boolean>(false);

  const checkSensorsAvailability = useCallback(async (): Promise<boolean> => {
    // TODO: implement the "Device Orientation and Motion" API for web platforms.
    // https://rbictg.atlassian.net/wiki/spaces/BKBP/pages/4860510232/Sensors+Not+Working+correctly+on+Web+Mobile
    if (isWeb) {
      return false;
    }

    const { granted: accelerometerGranted } = await Accelerometer.requestPermissionsAsync();
    const { granted: gyroscopeGranted } = await Gyroscope.requestPermissionsAsync();

    const allGranted = accelerometerGranted && gyroscopeGranted;

    if (!allGranted) {
      return false;
    }

    const accelerometerAvailable = await Accelerometer.isAvailableAsync();
    const gyroscopeAvailable = await Gyroscope.isAvailableAsync();
    return accelerometerAvailable && gyroscopeAvailable;
  }, []);

  const startStopSensors = useCallback(
    async ({ accelerometer, gyroscope }: StartStopSensorsConfig) => {
      setIsAccelerometerActive(prev => accelerometer ?? prev);
      setIsGyroscopeActive(prev => gyroscope ?? prev);
    },
    []
  );

  useEffect(() => {
    let accelerometerSubscription: { remove: () => void } | null = null;
    let gyroscopeSubscription: { remove: () => void } | null = null;

    const subscribeToSensor = async (
      sensor: SENSOR,
      sensorModule: typeof Accelerometer | typeof Gyroscope,
      isActive: boolean
    ) => {
      if (isActive) {
        sensorModule.setUpdateInterval(100);
        const listener = (newData: AccelerometerMeasurement | GyroscopeMeasurement) => {
          sensorDataRef.current[sensor] = newData;

          // Only send data when at least one sensor is active
          if (isAccelerometerActive || isGyroscopeActive) {
            handlePostMessageToIframe(
              appMessages.sensorData(JSON.stringify(sensorDataRef.current))
            );
          }
        };

        try {
          switch (sensor) {
            case SENSOR.ACCELEROMETER:
              accelerometerSubscription = Accelerometer.addListener(listener);
              break;
            case SENSOR.GYROSCOPE:
              gyroscopeSubscription = Gyroscope.addListener(listener);
              break;
          }
        } catch (error) {
          // Some platforms may have limited or no support for accelerometer and gyroscope features.
          handlePostMessageToIframe(appMessages.sensorsAvailability(false));
        }
      } else {
        switch (sensor) {
          case SENSOR.ACCELEROMETER:
            accelerometerSubscription?.remove();
            accelerometerSubscription = null;
            break;
          case SENSOR.GYROSCOPE:
            gyroscopeSubscription?.remove();
            gyroscopeSubscription = null;
            break;
        }
      }
    };

    const subscribeToSensors = async () => {
      await Promise.all([
        subscribeToSensor(SENSOR.ACCELEROMETER, Accelerometer, isAccelerometerActive),
        subscribeToSensor(SENSOR.GYROSCOPE, Gyroscope, isGyroscopeActive),
      ]);
    };

    subscribeToSensors();

    return () => {
      if (accelerometerSubscription) {
        accelerometerSubscription.remove();
      }
      if (gyroscopeSubscription) {
        gyroscopeSubscription.remove();
      }
    };
  }, [handlePostMessageToIframe, isAccelerometerActive, isGyroscopeActive]);

  return { startStopSensors, checkSensorsAvailability };
};
